import { type CompanyDetailType } from '@/api/activity';
import BulletSeparated from '@/components/BulletSeparated';
import { Sheet, SheetContent, SheetHeader } from '@/components/Sheet';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/Tooltip';
import { Link, Text } from '@/components/typography';
import { getRevenueRangeDescription } from '@/messages/revenue-range-name-messages';
import { ROUTES } from '@/router';

import CompanyLogo from './CompanyLogo';
import CompanyTotalVisitsPanel from './CompanyTotalVisitsPanel';
import CompanyTrendPanel from './CompanyTrendPanel';

type Props = {
  data: CompanyDetailType | null;
  startDate?: string;
  endDate?: string;
  onClose?: () => void;
};

const WebTrafficActivityDetailSheet = ({ data, startDate, endDate, onClose }: Props) => {
  const name = data?.name;
  const tld = data?.tld;
  const displayName = name ?? tld;
  const sectorName = data?.sector?.name;
  const revRangeDesc = getRevenueRangeDescription(data?.revRange?.id);

  const drawerOpen = data != null;

  return (
    <Sheet
      open={drawerOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose?.();
        }
      }}
    >
      <SheetContent
        side="right"
        className="gap-0 p-0 data-[side=right]:w-full data-[side=right]:sm:max-w-150"
      >
        <SheetHeader className="pt-10 pb-6">
          <div className="flex items-start gap-4">
            <CompanyLogo name={name} domain={tld} size="xlarge" />
            <div className="flex flex-1 flex-col">
              <Link
                className="w-fit"
                variant="label"
                weight="semi-bold"
                to={ROUTES.companyById(data?.id)}
              >
                {displayName}
              </Link>
              <div className="flex items-start justify-between">
                <Text variant="caption1" className="flex flex-1 flex-wrap gap-1 py-1 leading-3">
                  <BulletSeparated>
                    {[
                      sectorName && <span>{sectorName}</span>,
                      revRangeDesc && <span>{revRangeDesc}</span>,
                    ].filter(Boolean)}
                  </BulletSeparated>
                </Text>
                <Tooltip disabled={!tld}>
                  <TooltipTrigger
                    render={
                      <Link
                        className="max-w-50 truncate whitespace-nowrap"
                        to={`//${tld}`}
                        target="_blank"
                        variant="body2"
                        color="static-grey"
                        showExternalIcon
                      >
                        {tld}
                      </Link>
                    }
                  />
                  <TooltipContent>{tld}</TooltipContent>
                </Tooltip>
              </div>
            </div>
          </div>
        </SheetHeader>
        <div className="flex flex-col gap-6 overflow-y-auto px-6 py-6">
          <CompanyTrendPanel data={data} startDate={startDate} endDate={endDate} />
          <CompanyTotalVisitsPanel
            open={drawerOpen}
            data={data}
            startDate={startDate}
            endDate={endDate}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default WebTrafficActivityDetailSheet;
